import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  OnInit,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DocumentForm, DocumentState } from '@app/features/documents/document-form/document-form';
import { DocumentsMetadata } from '@app/features/documents/document-metadata.model';
import { CdTDocument, DocumentTag, DocumentTags } from '@app/features/documents/document.model';
import moment from 'moment';
import { map, startWith } from 'rxjs';

type DocumentsFormGroup = FormGroup<
  Record<DocumentTag, FormControl<CdTDocument[]>> &
    Record<keyof DocumentsMetadata, FormControl<string | null>>
>;
type ResidenceType = 'proofOfResidence' | 'proofOfAccommodation';
type IdType = 'identityDocument' | 'residentPermit';

export interface DocumentsFormValue {
  documents: CdTDocument[];
  metadata: DocumentsMetadata | null;
}

@Component({
  selector: 'app-documents-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    DocumentForm,
    MatButtonToggleModule,
    MatDatepickerModule,
  ],
  templateUrl: './documents-form.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./documents-form.scss'],
})
export class DocumentsForm implements OnInit {
  static concernedStates: DocumentState[] = ['provided', 'waiting'];
  ContractDocTags = DocumentTags;

  initWith = input<DocumentsFormValue>();
  companyCar = input.required<boolean>();
  readOnly = input<boolean>(false);
  validation = input<boolean>(false);
  uploaded = output<CdTDocument>();
  metadataChange = output<DocumentsMetadata>();

  cdr = inject(ChangeDetectorRef);

  form: DocumentsFormGroup = new FormGroup({
    residentPermitExpirationDate: new FormControl<string | null>(null),
    identityDocument: new FormControl<CdTDocument[]>([], { nonNullable: true }),
    residentPermit: new FormControl<CdTDocument[]>([], { nonNullable: true }),
    proofOfResidence: new FormControl<CdTDocument[]>([], { nonNullable: true }),
    proofOfAccommodation: new FormControl<CdTDocument[]>([], { nonNullable: true }),
    hostIdentityDocument: new FormControl<CdTDocument[]>([], { nonNullable: true }),
    hostProofOfResidence: new FormControl<CdTDocument[]>([], { nonNullable: true }),
    rib: new FormControl<CdTDocument[]>([], { nonNullable: true }),
    socialSecurityCertificate: new FormControl<CdTDocument[]>([], { nonNullable: true }),
    contract: new FormControl<CdTDocument[]>([], { nonNullable: true }),
    signedContract: new FormControl<CdTDocument[]>([], { nonNullable: true }),
    drivingLicense: new FormControl<CdTDocument[]>([], { nonNullable: true }),
    drivingLicenseExpirationDate: new FormControl<string | null>(null),
    securitySheet: new FormControl<CdTDocument[]>([], { nonNullable: true }),
    signedDrivingLicense: new FormControl<CdTDocument[]>([], { nonNullable: true }),
    signedSecuritySheet: new FormControl<CdTDocument[]>([], { nonNullable: true }),
    // Les pièces des avenants, rattachées au contrat : elles se lisent, l'onglet ne les dépose pas.
    amendment: new FormControl<CdTDocument[]>([], { nonNullable: true }),
    signedAmendment: new FormControl<CdTDocument[]>([], { nonNullable: true }),
  });

  constructor() {
    effect(() => this.valueSig());
    effect(() => {
      this.applyReadOnlyState(this.readOnly());
    });
    effect(() => {
      if (this.validation()) {
        this.form.markAllAsTouched();
      }
    });
  }

  readonly valueSig = toSignal(
    this.form.valueChanges.pipe(
      startWith(this.toDocumentsFormValue()),
      map(() => this.toDocumentsFormValue()),
    ),
    {
      initialValue: this.toDocumentsFormValue(),
    },
  );

  toDocumentsFormValue(): DocumentsFormValue {
    const documents = Object.entries(this.form.controls)
      .filter(
        ([key]) => key !== 'residentPermitExpirationDate' && key !== 'drivingLicenseExpirationDate',
      )
      .flatMap(([, control]) => (control as FormControl<CdTDocument[]>).value);

    return {
      documents,
      metadata: {
        residentPermitExpirationDate: this.form.controls.residentPermitExpirationDate.value,
        drivingLicenseExpirationDate: this.form.controls.drivingLicenseExpirationDate.value,
      },
    };
  }

  ngOnInit(): void {
    if (this.initWith()) {
      // Initialisation depuis les données existantes : ce n'est pas une saisie utilisateur,
      // aucun enregistrement ne doit en découler.
      this.form.controls.residentPermitExpirationDate.setValue(
        this.initWith()!.metadata?.residentPermitExpirationDate ?? null,
        { emitEvent: false },
      );
      this.form.controls.drivingLicenseExpirationDate.setValue(
        this.initWith()!.metadata?.drivingLicenseExpirationDate ?? null,
        { emitEvent: false },
      );
      // Regroup documents by tag to set form values
      const docsByTag = this.initWith()!.documents.reduce(
        (acc, doc) => {
          const tag = doc.tag;
          if (!acc[tag]) {
            acc[tag] = [];
          }
          acc[tag].push(doc);
          return acc;
        },
        {} as Record<DocumentTag, CdTDocument[]>,
      );

      Object.keys(docsByTag).forEach((tag) => {
        this.form
          .get(tag as DocumentTag)
          ?.setValue(docsByTag[tag as DocumentTag], { emitEvent: false });
      });

      if (this.form.controls.proofOfResidence.value.length > 0) {
        this.onResidenceTypeChange('proofOfResidence');
      }
      if (this.form.controls.proofOfAccommodation.value.length > 0) {
        this.onResidenceTypeChange('proofOfAccommodation');
      }
      if (this.form.controls.identityDocument.value.length > 0) {
        this.onIdTypeChange('identityDocument');
      }
      if (
        this.form.controls.residentPermit.value.length > 0 ||
        this.form.controls.residentPermitExpirationDate.value
      ) {
        this.onIdTypeChange('residentPermit');
      }
      setTimeout(() => {
        this.onIdTypeChange(this.idType);
        this.onResidenceTypeChange(this.residenceType);
        this.cdr.markForCheck();
      }, 500);

      this.form.valueChanges.subscribe(() => {
        this.form.updateValueAndValidity({ emitEvent: false });
      });
      this.form.controls.residentPermitExpirationDate.valueChanges.subscribe((value) => {
        this.metadataChange.emit({
          residentPermitExpirationDate: value,
          drivingLicenseExpirationDate: this.form.controls.drivingLicenseExpirationDate.value,
        });
      });
      this.form.controls.drivingLicenseExpirationDate.valueChanges.subscribe((value) => {
        this.metadataChange.emit({
          residentPermitExpirationDate: this.form.controls.residentPermitExpirationDate.value,
          drivingLicenseExpirationDate: value,
        });
      });
    }
  }

  residenceType: ResidenceType = 'proofOfResidence';
  idType: IdType = 'identityDocument';

  onResidenceTypeChange(value: ResidenceType): void {
    this.residenceType = value;
    // `emitEvent: false` : activer/désactiver un contrôle ne change pas la saisie de
    // l'utilisateur et ne doit donc déclencher aucun enregistrement.
    if (this.residenceType === 'proofOfResidence') {
      this.form.controls.proofOfResidence.enable({ emitEvent: false });
      this.form.controls.proofOfAccommodation.disable({ emitEvent: false });
      this.form.controls.hostProofOfResidence.disable({ emitEvent: false });
      this.form.controls.hostIdentityDocument.disable({ emitEvent: false });
    } else {
      this.form.controls.proofOfResidence.disable({ emitEvent: false });
      this.form.controls.proofOfAccommodation.enable({ emitEvent: false });
      this.form.controls.hostProofOfResidence.enable({ emitEvent: false });
      this.form.controls.hostIdentityDocument.enable({ emitEvent: false });
    }
  }

  onIdTypeChange(value: IdType): void {
    this.idType = value;
    // Idem : `residentPermitExpirationDate` porte la métadonnée enregistrée côté API, un
    // `emitEvent: true` ici déclenchait un PUT documents-metadata à la simple ouverture.
    if (this.idType === 'identityDocument') {
      this.form.controls.identityDocument.enable({ emitEvent: false });
      this.form.controls.residentPermitExpirationDate.disable({ emitEvent: false });
      this.form.controls.residentPermit.disable({ emitEvent: false });
    } else {
      this.form.controls.identityDocument.disable({ emitEvent: false });
      this.form.controls.residentPermit.enable({ emitEvent: false });
      this.form.controls.residentPermitExpirationDate.enable({ emitEvent: false });
    }
  }

  get showHostFields(): boolean {
    return (
      this.form.controls.proofOfAccommodation.value.length > 0 &&
      this.form.controls.hostIdentityDocument.value.length > 0 &&
      this.form.controls.hostProofOfResidence.value.length > 0
    );
  }

  onChange(document: CdTDocument[]): void {
    console.log('DocumentForm onChange called with:', document);
  }

  updateValueAndValidity(documentTag: DocumentTag): void {
    this.form.controls[documentTag].markAsTouched();
    this.form.controls[documentTag].updateValueAndValidity();
  }

  private applyReadOnlyState(readOnly: boolean): void {
    // Bascule d'état d'affichage, pas une saisie : `emitEvent: true` déclenchait un
    // enregistrement des métadonnées à la simple ouverture du contrat.
    if (readOnly) {
      this.form.disable({ emitEvent: false });
      return;
    }
    this.form.enable({ emitEvent: false });
    this.onIdTypeChange(this.idType);
    this.onResidenceTypeChange(this.residenceType);
  }

  onPreviousContractSelected(documents: CdTDocument[]): void {
    const filteredDocuments = documents.filter((doc) => {
      if (doc.tag === 'contract' || doc.tag === 'signedContract') {
        return false;
      }

      if (
        [
          'proofOfResidence',
          'proofOfAccommodation',
          'hostIdentityDocument',
          'hostProofOfResidence',
          'rib',
        ].includes(doc.tag)
      ) {
        return this.isDocumentFromLastThreeMonths(doc);
      }

      return true;
    });

    // Regroup documents by tag to set form values
    const docsByTag = filteredDocuments.reduce(
      (acc, doc) => {
        const tag = doc.tag;
        if (!acc[tag]) {
          acc[tag] = [];
        }
        acc[tag].push(doc);
        return acc;
      },
      {} as Record<DocumentTag, CdTDocument[]>,
    );

    Object.keys(docsByTag).forEach((tag) => {
      const control = this.form.get(tag as DocumentTag);
      if (!control) {
        return;
      }

      const existingDocuments = control.value || [];
      const mergedDocuments = [...existingDocuments];
      docsByTag[tag as DocumentTag].forEach((doc) => {
        if (!mergedDocuments.some((existingDoc) => existingDoc.id === doc.id)) {
          mergedDocuments.push(doc);
        }
      });

      control.setValue(mergedDocuments);
    });

    if (this.form.controls.proofOfResidence.value.length > 0) {
      this.onResidenceTypeChange('proofOfResidence');
    } else if (this.form.controls.proofOfAccommodation.value.length > 0) {
      this.onResidenceTypeChange('proofOfAccommodation');
    }
    if (this.form.controls.identityDocument.value.length > 0) {
      this.onIdTypeChange('identityDocument');
    } else if (this.form.controls.residentPermit.value.length > 0) {
      this.onIdTypeChange('residentPermit');
    }
  }

  private isDocumentFromLastThreeMonths(document: CdTDocument): boolean {
    const createdAt = moment(document.createdAt);
    if (!createdAt.isValid()) {
      return false;
    }
    return createdAt.isSameOrAfter(moment().subtract(3, 'months'));
  }
}
